import { Select } from "@shared/components";
import React, { useEffect, useMemo } from "react";
import { actions, selectors as SharedSelectors } from "@shared/store";
import { useDispatch, useSelector } from "react-redux";
import { PaymentMethod, PaymentMethodType } from "@shared/interfaces";

import "./index.scss";

// Helper to format card number
const formatCardNumber = (cardNumber: string): string => cardNumber.replace(/(.{4})/g, "$1 ").trim();

const getPrimaryPaymentMethod = (methods?: PaymentMethod[]): PaymentMethod | undefined => {
  if (!methods || !methods.length) return;
  return methods.find((method) => method.primary) || methods[0];
};

interface CardOption {
  value: string; // Card ID
  label: string; // Masked card number
}

interface CardSelectorProps {
  value?: string | null; // Currently selected card ID
  onChange?: (value: string | null) => void;
}

export const CardSelector: React.FC<CardSelectorProps> = ({ value, onChange }) => {
  const dispatch = useDispatch();
  const accountPaymentMethods = useSelector(SharedSelectors.getAccountPaymentMethods());
  const cards = useMemo<CardOption[]>(() => {
    return (accountPaymentMethods || []).map((p) => ({
      value: p.id,
      label: p.card?.masked_number ? formatCardNumber(p.card.masked_number) : "Unknown Card",
    }));
  }, [accountPaymentMethods]);

  useEffect(() => {
    dispatch(actions.clearAccountPaymentMethods());
    dispatch(actions.getAccountPaymentMethods.request({ type: PaymentMethodType.Card }));
  }, [dispatch]);

  // Preselect the primary card if no value is selected
  useEffect(() => {
    if (!value) {
      const primaryPaymentMethod = getPrimaryPaymentMethod(accountPaymentMethods);
      if (primaryPaymentMethod) onChange?.(primaryPaymentMethod.id);
    }
  }, [accountPaymentMethods, value, onChange]);

  return (
    <div className="card-selector">
      <label>Payment method</label>
      <Select
        value={value ? cards.find((card) => card.value === value) : undefined}
        onChange={(selectedOption: unknown) => {
          if (onChange) onChange(selectedOption as string);
        }}
        isSearchable
        isDisabled={!cards.length}
        options={cards}
        placeholder="Select"
        noOptionsMessage={() => "No matches found"}
        className="card-selector-input"
      />
    </div>
  );
};
